import React from 'react';
import { DocumentIcon, ArrowDownTrayIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { UserAvatar } from 'src/components/ui/avatar/Avatar';
import { useAppSelector } from 'src/hooks/redux';
import { selectUsers } from 'src/modules/users/selector';
import { BACKEND_URL } from '../../api/config';

interface CommentItemProps {
  comment: {
    id: number;
    documentId: string;
    Title: string;
    CommentId: number;
    FullName?: string;
    EmailUser?: string;
    Docs?: Array<{
      name: string;
      url: string;
      size: number;
    }>;
    createdAt?: string;
  };
  onEdit?: (documentId: string) => void;
  onDelete?: (documentId: string) => void;
}

export function CommentItem({ comment, onEdit, onDelete }: CommentItemProps) {
  const users: any = useAppSelector(selectUsers);
  const handleDownload = async (fileUrl: string, fileName: string) => {
      const fullUrl = `${BACKEND_URL}${fileUrl}` ;
      const link = document.createElement('a');
      link.href = fullUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  };

  const formatFileSize = (sizeInKB: number) => {
    if (!sizeInKB || sizeInKB === 0) return '0 MB';
    const sizeInMB = sizeInKB / 1024;
    return `${sizeInMB.toFixed(2)} MB`;
  };

  const handleDeleteClick = () => {
    if (onDelete && comment.documentId) {
      onDelete(comment.documentId);
    }
  };

  const handleEditClick = () => {
    if (onEdit && comment.documentId) {
      onEdit(comment.documentId);
    }
  };

  const formatCommentText = (text: string) => {
    return text.split('\n').map((line, index, array) => (
      <React.Fragment key={index}>
        {line}
        {index < array.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <div className='mb-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm'>
      <div className='flex items-center justify-between mb-4'>
        <div className='flex items-center space-x-3'>
          <UserAvatar
            avatar={comment.EmailUser && users[comment.EmailUser] && users[comment.EmailUser].avatar}
            shortName={comment.EmailUser && users[comment.EmailUser] && users[comment.EmailUser].shortName}
            displayName={comment.EmailUser && users[comment.EmailUser] && users[comment.EmailUser].displayName}
            email={comment.EmailUser || ''}
          />
          <div className='flex flex-col'>
            <span className='font-medium text-gray-900'>{comment.FullName}</span>
            <span className='text-sm text-gray-500'>{comment.createdAt ? new Date(comment.createdAt).toLocaleDateString('ru-RU') : new Date().toLocaleDateString('ru-RU')}</span>
          </div>
        </div>

        <div className='flex space-x-2'>
          {onEdit && (
            <button onClick={handleEditClick} className='p-2 text-gray-400 hover:text-blue-600 hover:bg-gray-100 rounded' title='Редактировать'>
              <PencilIcon className='w-4 h-4' />
            </button>
          )}
          {onDelete && (
            <button onClick={handleDeleteClick} className='p-2 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded' title='Удалить комментарий'>
              <TrashIcon className='w-4 h-4' />
            </button>
          )}
        </div>
      </div>

      <div className='mb-4'>
        <p className='text-gray-700'>{formatCommentText(comment.Title)}</p>
      </div>

      {comment.Docs && Array.isArray(comment.Docs) && comment.Docs.length > 0 && (
        <div className='bg-gray-50 rounded-lg p-3 space-y-2'>
          {comment.Docs.map((doc, index) => (
            <div key={index} className='flex items-center justify-between cursor-pointer hover:bg-gray-100 rounded p-2 -m-2' onClick={() => handleDownload(doc.url, doc.name)}>
              <div className='flex items-center space-x-3'>
                <DocumentIcon className='w-5 h-5 text-gray-400' />
                <div>
                  <span className='text-sm font-medium text-gray-900'>{doc.name.length > 30 ? `${doc.name.substring(0, 30)}...${doc.name.split('.').pop()}` : doc.name}</span>
                  <span className='text-sm text-gray-500 ml-2'>({formatFileSize(doc.size)})</span>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(doc.url, doc.name);
                }}
                className='p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded'
              >
                <ArrowDownTrayIcon className='w-4 h-4' />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
